import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams as useRouteParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import type { OnChangeFn, Row, SortingState } from "@tanstack/react-table";
import type { DateValueType } from "react-tailwindcss-datepicker";
import AppLayout from "@/layouts/AppLayout";
import Breadcrumb from "@/components/Breadcrumb";
import DateSelect from "@/components/DateSelect";
import EmptyState from "@/components/EmptyState";
import ErrorBoundary from "@/components/ErrorBoundary";
import SearchInput from "@/components/SearchInput";
import Table, { TableControls, TableFooter } from "@/components/Table";
import InteractionSideSheet from "@/features/global/components/InteractionSideSheet";
import NoteSideSheet from "@/features/global/components/NoteSideSheet/NoteSideSheet";
import { PersonObjectHeading } from "@/features/person/components/PersonObjectHeading";
import usePagination from "@/hooks/usePagination";
import { useParams as useUrlParams, useSearchWithParams } from "@/hooks/useSearchParams";
import PersonNotFoundErrorBoundary from "../../_components/PersonNotFoundErrorBoundary";
import { usePersonErrorHandling } from "../../_components/usePersonErrorHandling";
import TabLayout from "../_TabLayout";
import { getColumns } from "./_components/TimelineTableColumns";
import { PERSON_TIMELINE_QUERY, type PersonTimelineQueryVariables } from "./queries";
import { showSnackbar } from "@/lib/snackbarUtils";
import { canOpenTimelineRow, PersonTimelineObjectType, type PersonTimelineRow } from "./types";
import { getTimelineObjectId } from "./utils";

const DEFAULT_SORTING: SortingState = [{ id: "occurredAt", desc: true }];

const sortFields: Record<string, NonNullable<PersonTimelineQueryVariables["orderBy"]>["field"]> = {
  objectType: "OBJECT_TYPE",
  title: "TITLE",
  description: "DESCRIPTION",
  sourceLabel: "SOURCE_LABEL",
  authorName: "AUTHOR_NAME",
  occurredAt: "OCCURRED_AT"
};

function toDateRangeParam(value: DateValueType) {
  if (!value || typeof value === "string") return undefined;

  const from = value.startDate ? new Date(value.startDate).toISOString().split("T")[0] : undefined;
  const to = value.endDate ? new Date(value.endDate).toISOString().split("T")[0] : from;

  return from || to ? `${from || ""},${to || ""}` : undefined;
}

function dateRangeParamToValue(value?: string): DateValueType {
  if (!value) return { startDate: null, endDate: null };

  const [from, to] = value.split(",");
  return {
    startDate: from || null,
    endDate: to || from || null
  };
}

function dateRangeParamToInput(value?: string) {
  if (!value) return undefined;

  const [from, to] = value.split(",");
  if (!from && !to) return undefined;

  return {
    ...(from && { from }),
    ...(to && { to })
  };
}

function parseObjectTypes(value: unknown): PersonTimelineObjectType[] {
  if (!Array.isArray(value)) return [];

  return value.filter((item): item is PersonTimelineObjectType =>
    Object.values(PersonTimelineObjectType).includes(item as PersonTimelineObjectType)
  );
}

function PersonHistoryTable() {
  const { pgaId } = useRouteParams();
  const navigate = useNavigate();
  const { params, addSearchParam, deleteSearchParams } = useUrlParams();
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "personHistorySearch"
  });
  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination();
  const [sortingState, setSortingState] = useState<SortingState>(DEFAULT_SORTING);
  const [selectedRow, setSelectedRow] = useState<PersonTimelineRow | null>(null);
  const hasMountedRef = useRef(false);

  const selectedObjectTypes = useMemo(() => parseObjectTypes(params.objectType), [params.objectType]);
  const selectedObjectTypesKey = selectedObjectTypes.join(",");
  const dateRangeParam = typeof params.dateRange === "string" ? params.dateRange : undefined;
  const dateRangeValue = useMemo(() => dateRangeParamToValue(dateRangeParam), [dateRangeParam]);

  const orderBy = useMemo<PersonTimelineQueryVariables["orderBy"]>(() => {
    const sort = sortingState[0] ?? DEFAULT_SORTING[0];
    return {
      field: sortFields[sort.id] || "OCCURRED_AT",
      direction: sort.desc ? "DESC" : "ASC"
    };
  }, [sortingState]);

  const input = useMemo<PersonTimelineQueryVariables["input"]>(() => {
    const nextInput: NonNullable<PersonTimelineQueryVariables["input"]> = {
      ...(debouncedSearch && { search: debouncedSearch }),
      ...(selectedObjectTypes.length > 0 && {
        objectTypes: selectedObjectTypes
      }),
      ...(dateRangeParamToInput(dateRangeParam) && {
        dateRange: dateRangeParamToInput(dateRangeParam)
      })
    };

    return Object.keys(nextInput).length > 0 ? nextInput : undefined;
  }, [dateRangeParam, debouncedSearch, selectedObjectTypes]);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }

    resetPagination();
  }, [debouncedSearch, selectedObjectTypesKey, dateRangeParam, orderBy, resetPagination]);

  const { data, loading, error } = useQuery(PERSON_TIMELINE_QUERY, {
    variables: {
      pgaId: pgaId || "",
      ...paginationVariables,
      input,
      orderBy
    },
    skip: !pgaId
  });

  const timeline = data?.personTimeline;
  const rows = timeline?.nodes ?? [];
  const hasActiveFilters = !!debouncedSearch || selectedObjectTypes.length > 0 || !!dateRangeParam;

  const handleOpenRow = useCallback((row: PersonTimelineRow) => {
    if (!canOpenTimelineRow(row)) {
      if (row.objectType === PersonTimelineObjectType.INTERACTION) {
        showSnackbar("Access to this interaction is restricted", { variant: "warning" });
      }
      return;
    }

    setSelectedRow(row);
  }, []);

  const handleCloseSideSheet = useCallback(() => {
    setSelectedRow(null);
  }, []);

  const handleViewSource = useCallback(() => {
    if (!selectedRow?.sourcePath) return;
    navigate(selectedRow.sourcePath);
  }, [navigate, selectedRow?.sourcePath]);

  const columns = useMemo(() => getColumns({ onViewRow: handleOpenRow }), [handleOpenRow]);

  function setObjectTypeFilter(type: PersonTimelineObjectType) {
    const nextTypes = selectedObjectTypes.includes(type)
      ? selectedObjectTypes.filter((selectedType) => selectedType !== type)
      : [...selectedObjectTypes, type];

    if (nextTypes.length > 0) {
      addSearchParam("objectType[]", nextTypes.join(","));
    } else {
      deleteSearchParams("objectType[]");
    }
    resetPagination();
  }

  function setDateRange(value: DateValueType) {
    const nextValue = toDateRangeParam(value);
    if (nextValue) {
      addSearchParam("dateRange", nextValue);
    } else {
      deleteSearchParams("dateRange");
    }
    resetPagination();
  }

  const handleSortingChange: OnChangeFn<SortingState> = (updaterOrValue) => {
    setSortingState(updaterOrValue);
    resetPagination();
  };

  const selectedObjectId = selectedRow ? getTimelineObjectId(selectedRow) : null;
  const isInteractionSideSheetOpen = selectedRow?.objectType === PersonTimelineObjectType.INTERACTION;
  const isNoteSideSheetOpen = selectedRow?.objectType === PersonTimelineObjectType.NOTE;

  if (error) {
    return (
      <ErrorBoundary variant="inline" title="Unable to Load Person History" message={error.message} className="mt-12" />
    );
  }

  return (
    <>
      <div className="pt-12" />
      <Table
        data={rows}
        columns={columns}
        loading={loading}
        serverSideSorting
        sortingState={sortingState}
        onSortingChange={handleSortingChange}
        onClickRow={(row: Row<PersonTimelineRow>) => handleOpenRow(row.original)}
        getRowClasses={(row) => (canOpenTimelineRow(row.original) ? "" : "cursor-default")}
        hasActiveFilters={hasActiveFilters}
        renderNoDataEmptyState={() => (
          <EmptyState
            iconName="timeline"
            title="No Person History"
            caption="Engagements and milestones will appear here as history becomes available."
          />
        )}
        renderFilteredEmptyState={() => (
          <EmptyState
            iconName="search"
            title="No Person History Results"
            caption="Try a different search, date range, or type filter."
          />
        )}
        renderControls={(_table, { onSortClick } = {}) => (
          <TableControls
            pagination={createHeaderPagination(timeline, { loading })}
            onSortClick={onSortClick}
            useExpandedFilters
            expandedFiltersState={{ dateRange: !!dateRangeParam }}
            activeFilters={[dateRangeParam, debouncedSearch, selectedObjectTypesKey].filter(Boolean).length}
            onClearAllFilters={() => {
              clearSearch();
              deleteSearchParams(["objectType[]", "dateRange"]);
              resetPagination();
            }}
            filterChips={[
              {
                label: "Notes",
                onClick: () => setObjectTypeFilter(PersonTimelineObjectType.NOTE),
                selected: selectedObjectTypes.includes(PersonTimelineObjectType.NOTE)
              },
              {
                label: "Interactions",
                onClick: () => setObjectTypeFilter(PersonTimelineObjectType.INTERACTION),
                selected: selectedObjectTypes.includes(PersonTimelineObjectType.INTERACTION)
              }
            ]}
            mutuallyExclusiveChips={false}
          >
            <div aria-label="Search Person History">
              <SearchInput
                query={searchValue}
                onChange={(value) => {
                  setSearchValue(value);
                  resetPagination();
                }}
                onClear={() => {
                  clearSearch();
                  resetPagination();
                }}
                placeholder="Search Person History"
              />
            </div>
            <div data-name="dateRange" data-filter-size="custom">
              <DateSelect
                label="Date Range"
                value={dateRangeValue}
                onChange={(value) => setDateRange(value as DateValueType)}
                placeholder="Show All"
                popoverDirection="down"
                useModal
                useDefaultShortcuts
              />
            </div>
          </TableControls>
        )}
        renderFooter={() => (
          <TableFooter
            pagination={createFooterPagination(timeline, { loading })}
            totalRowCount={timeline?.totalCount ?? rows.length}
          />
        )}
      />
      <NoteSideSheet
        open={isNoteSideSheetOpen}
        onClose={handleCloseSideSheet}
        noteId={isNoteSideSheetOpen ? selectedObjectId : null}
        customActionButtons={
          selectedRow?.sourcePath
            ? [
                {
                  text: "View Note",
                  variant: "outlined",
                  onClick: handleViewSource,
                  testId: "view-person-history-source"
                }
              ]
            : undefined
        }
      />
      <InteractionSideSheet
        open={isInteractionSideSheetOpen}
        onClose={handleCloseSideSheet}
        interactionId={isInteractionSideSheetOpen ? selectedObjectId : null}
        pgaId={pgaId}
        customActionButtons={
          selectedRow?.sourcePath
            ? [
                {
                  text: "View Interaction",
                  variant: "outlined",
                  onClick: handleViewSource,
                  testId: "view-person-history-source"
                }
              ]
            : undefined
        }
      />
    </>
  );
}

export default function PersonHistoryPageContainer() {
  const { pgaId, navLinks, shouldRenderError } = usePersonErrorHandling();

  if (shouldRenderError) {
    return (
      <AppLayout navLinks={[]} interior bottomBar>
        <PersonNotFoundErrorBoundary />
      </AppLayout>
    );
  }

  return (
    <AppLayout navLinks={navLinks} interior bottomBar>
      <Breadcrumb
        compactOnly
        compactTitle="Person History"
        backLink={{ to: "/people/:pgaId", params: { pgaId: pgaId! } }}
      />
      <PersonObjectHeading pgaId={pgaId!} />
      <TabLayout>
        <PersonHistoryTable />
      </TabLayout>
    </AppLayout>
  );
}
